package app.nestbridge.feature.children.presentation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import app.nestbridge.app.util.launchSafely
import app.nestbridge.feature.careteam.model.CareTeam
import app.nestbridge.feature.careteam.usecase.FindCareTeamsByChildIdUseCase
import app.nestbridge.feature.careteam.screen.CreateCareTeamScreen
import app.nestbridge.feature.children.model.Child
import app.nestbridge.feature.children.usecase.GetChildUseCase
import app.nestbridge.feature.children.screen.ChildDetailEvent
import app.nestbridge.feature.children.screen.ChildDetailScreen
import app.nestbridge.feature.children.screen.ChildDetailState
import app.nestbridge.feature.invitations.model.Invitation
import app.nestbridge.feature.invitations.usecase.GetPendingTeamCreationInvitationUseCase
import app.nestbridge.feature.invitations.usecase.RescindInvitationUseCase
import app.nestbridge.feature.invitations.screen.SendInvitationScreen
import app.nestbridge.feature.invitations.screen.ShowInvitationQRCodeScreen
import app.nestbridge.domain.identity.usecase.GetCurrentProfessionalProfileUseCase
import app.nestbridge.feature.profile.screen.CreateProfessionalProfileResult
import app.nestbridge.feature.profile.screen.CreateProfessionalProfileScreen
import com.slack.circuit.codegen.annotations.CircuitInject
import com.slack.circuit.foundation.rememberAnsweringNavigator
import com.slack.circuit.runtime.Navigator
import com.slack.circuit.runtime.presenter.Presenter
import me.tatarka.inject.annotations.Assisted
import me.tatarka.inject.annotations.Inject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope

@CircuitInject(
    screen = ChildDetailScreen::class,
    scope = AppScope::class,
)
@Inject
class ChildDetailPresenter(
    @Assisted private val screen: ChildDetailScreen,
    @Assisted private val navigator: Navigator,
    private val getChildUseCase: GetChildUseCase,
    private val findCareTeamsByChildIdUseCase: FindCareTeamsByChildIdUseCase,
    private val getPendingTeamCreationInvitationUseCase: GetPendingTeamCreationInvitationUseCase,
    private val rescindInvitationUseCase: RescindInvitationUseCase,
    private val getCurrentProfessionalProfileUseCase: GetCurrentProfessionalProfileUseCase,
) : Presenter<ChildDetailState> {

    @Composable
    override fun present(): ChildDetailState {
        var child by remember { mutableStateOf(Child.empty) }
        var careTeams by remember { mutableStateOf<List<CareTeam>>(emptyList()) }
        var pendingInvitation by remember { mutableStateOf<Invitation?>(null) }
        var isLoading by remember { mutableStateOf(true) }
        var hasProfessionalProfile by remember { mutableStateOf(false) }

        suspend fun loadChildDetail() {
            isLoading = true
            child = getChildUseCase(screen.childId)
                ?: error("Missing child for ${screen.childId}")
            careTeams = findCareTeamsByChildIdUseCase(screen.childId)
            pendingInvitation = getPendingTeamCreationInvitationUseCase(screen.childId).getOrNull()
            hasProfessionalProfile = getCurrentProfessionalProfileUseCase() != null
            isLoading = false
        }

        val professionalProfileNavigator = rememberAnsweringNavigator<CreateProfessionalProfileResult>(navigator) { result ->
            // After creating professional profile, navigate to create team if successful
            if (result.status == CreateProfessionalProfileResult.Status.Created) {
                navigator.goTo(CreateCareTeamScreen(childId = screen.childId))
            }
        }

        fun onEvent(event: ChildDetailEvent) {
            when (event) {
                ChildDetailEvent.OnAppear -> {
                    kotlinx.coroutines.MainScope().launchSafely(onError = { isLoading = false }) {
                        loadChildDetail()
                    }
                }
                ChildDetailEvent.Back -> {
                    navigator.pop()
                }
                ChildDetailEvent.InviteProfessional -> {
                    navigator.goTo(SendInvitationScreen(childId = screen.childId))
                }
                ChildDetailEvent.CreateCareTeamMyself -> {
                    // Check if user has professional profile
                    if (hasProfessionalProfile) {
                        navigator.goTo(CreateCareTeamScreen(childId = screen.childId))
                    } else {
                        // Navigate to create professional profile first
                        professionalProfileNavigator.goTo(
                            CreateProfessionalProfileScreen(childId = screen.childId)
                        )
                    }
                }
                ChildDetailEvent.ViewInvitationQRCode -> {
                    pendingInvitation?.let { invitation ->
                        navigator.goTo(ShowInvitationQRCodeScreen(invitationId = invitation.id))
                    }
                }
                ChildDetailEvent.CancelInvitation -> {
                    kotlinx.coroutines.MainScope().launchSafely(onError = { isLoading = false }) {
                        pendingInvitation?.let { invitation ->
                            rescindInvitationUseCase(invitation.id)
                            loadChildDetail()
                        }
                    }
                }
            }
        }

        return ChildDetailState(
            child = child,
            careTeams = careTeams,
            pendingInvitation = pendingInvitation,
            isLoading = isLoading,
            eventSink = ::onEvent,
        )
    }
}
